import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { END_REACHED_VIEWPORT_RATIO } from "./lib";
import type { ScreenLayoutBodyHandle, ScreenLayoutBodyProps } from "./types";

/**
 * ScreenLayout.Body — web.
 *
 * The scrollable variant is a contained `overflow-auto` region (the
 * ScreenLayout root is a viewport-sized flex column), so body content
 * scrolls within its own bounds and never slides under the Header or
 * Actions — matching the native ScrollView. `refresh` is accepted but a
 * no-op on web. `onEndReached` fires within 50% viewport of the bottom.
 * Both variants expose a `ScreenLayoutBodyHandle` ref.
 */
export const ScreenLayoutBody = forwardRef<ScreenLayoutBodyHandle, ScreenLayoutBodyProps>(
  (props, ref) =>
    props.scrollable ? (
      <ScrollableBody ref={ref} {...props} />
    ) : (
      <StaticBody ref={ref} {...props} />
    ),
);

ScreenLayoutBody.displayName = "ScreenLayout.Body";

type StaticProps = Extract<ScreenLayoutBodyProps, { scrollable?: false }>;
type ScrollableProps = Extract<ScreenLayoutBodyProps, { scrollable: true }>;

const StaticBody = forwardRef<ScreenLayoutBodyHandle, StaticProps>(
  ({ className, testID, children }, ref) => {
    useImperativeHandle(ref, () => ({ scrollToTop: () => {} }), []);

    return (
      <Box
        {...platformProps({ testID, slot: "screen-layout-body" })}
        className={cn("flex min-h-0 flex-1 flex-col", className)}
      >
        {children}
      </Box>
    );
  },
);

StaticBody.displayName = "ScreenLayout.Body.Static";

const ScrollableBody = forwardRef<ScreenLayoutBodyHandle, ScrollableProps>(
  ({ className, testID, onEndReached, children }, ref) => {
    const localRef = useRef<HTMLDivElement | null>(null);
    const onEndReachedRef = useRef(onEndReached);
    const endReachedFiredRef = useRef(false);

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop: ({ animated = true } = {}) =>
          localRef.current?.scrollTo({ top: 0, behavior: animated ? "smooth" : "auto" }),
      }),
      [],
    );

    useEffect(() => {
      onEndReachedRef.current = onEndReached;
    }, [onEndReached]);

    useEffect(() => {
      const el = localRef.current;
      if (!el) return;

      const handler = () => {
        const fn = onEndReachedRef.current;
        if (!fn) return;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const threshold = el.clientHeight * END_REACHED_VIEWPORT_RATIO;
        if (distanceFromBottom <= threshold) {
          if (!endReachedFiredRef.current) {
            endReachedFiredRef.current = true;
            fn();
          }
        } else {
          endReachedFiredRef.current = false;
        }
      };

      el.addEventListener("scroll", handler, { passive: true });
      return () => el.removeEventListener("scroll", handler);
    }, []);

    return (
      <div
        ref={localRef}
        {...platformProps({ testID })}
        tabIndex={0}
        className={cn("flex min-h-0 flex-1 flex-col overflow-auto", className)}
      >
        {children}
      </div>
    );
  },
);

ScrollableBody.displayName = "ScreenLayout.Body.Scrollable";
